import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';
import ConsoleCard from './ConsoleCard';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectCurrentTaskAIStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import ConfirmDialog from '../common/ConfirmDialog';
import { MessageContainer } from '../Chat/MessageContainer';
import useTranscriptMessages from '../TranscriptSliderArea/useTranscriptMessages.hooks';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  // Transcript section: heading + inner white card. The grey card backdrop is
  // the shared left-pane card (MainPageLayout), so this stays transparent. The
  // takeover expansion is applied to that card (not this section), so the
  // reminder + AI status sections stay visible alongside the transcript.
  section: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // The transcript message stream, below the header divider (supplied by
  // ConsoleCard). The body just adds the matching top breathing room below that
  // line, and horizontal room for the bubbles' tails.
  messages: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Horizontal room for the bubbles' protruding WhatsApp tails (they extend
    // ~6px past the bubble edge); overflowX hidden so that never shows an
    // x-scrollbar. Clipping is at the padding box, so the tails stay visible.
    overflowX: 'hidden',
    padding: HITL3_PADDING,
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(1),
  },
  divider: {
    fontSize: '14px',
    lineHeight: '20px',
    color: Colors.guardsmanRed,
  },
  underline: {
    flex: 1,
    padding: '0.75px',
    backgroundColor: Colors.guardsmanRed,
    margin: '5px',
    display: 'inline-block',
  },
}));

/**
 * LIVE TRANSCRIPT (left pane). A single card: header (chat icon + label +
 * accordion chevron) above an inset divider, then the AI/customer message
 * stream (supervised-agent divider + click-to-report ASR-missing flow).
 * Collapsing via the chevron hides the stream, leaving just the header.
 *
 * NOTE: the volume control has been moved out of here for now.
 */
interface LiveTranscriptCardProps {
  // Accordion collapse, mirroring Customer Pulse / Action Center. Controlled
  // (open + onOpenChange) so the parent can sync the resizable panel; falls back
  // to internal state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const LiveTranscriptCard = ({
  open: openProp,
  onOpenChange,
}: LiveTranscriptCardProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);

  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const { messagesList, sessionBufferMessagesList } = useTranscriptMessages();
  const { isStaffInterventionHappening } = useIntervention();
  // Staff intervention hands the conversation to a human, so the AI transcript is
  // no longer the live record — blank it out. This is a view-layer clear only:
  // the underlying frames stay in Redux, so if the intervention resolves the
  // stream reappears intact (no data destroyed).
  const displayedMessages = isStaffInterventionHappening ? [] : messagesList;
  const displayedSessionBuffer = isStaffInterventionHappening
    ? []
    : sessionBufferMessagesList;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleTranscriptAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // During staff intervention the transcript is blanked, so there is no word
      // to report as ASR-incorrect — don't surface the "ASR MISSING" confirm.
      if (isStaffInterventionHappening) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        typeof document !== 'undefined' &&
        document.querySelector('[role="dialog"]')
      ) {
        return;
      }
      if (
        target &&
        (target.closest('[data-word]') || target.closest('[role="dialog"]'))
      ) {
        return;
      }
      setConfirmDialogOpen(true);
    },
    [isStaffInterventionHappening]
  );

  const handleConfirmBlankClick = useCallback(() => {
    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked: 'ASR MISSING',
      dispatch,
    });
    setConfirmDialogOpen(false);
  }, [
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  return (
    <div className={classes.section}>
      <ConsoleCard
        icon={<ChatBubbleOutlineOutlinedIcon />}
        title="Transcript"
        open={open}
        onToggle={toggleOpen}
        // Keep the message stream mounted (hidden) so collapse/expand is instant
        // and messages aren't re-rendered from scratch on every expand.
        keepBodyMounted
      >
        <div className={classes.messages} onClick={handleTranscriptAreaClick}>
          {displayedSessionBuffer.length > 0 && (
            <MessageContainer messageList={displayedSessionBuffer} compact />
          )}
          {currentTaskAIStatus && displayedSessionBuffer.length > 0 && (
            <div className={classes.dividerContainer}>
              <div className={classes.underline} />
              <div className={classes.divider}>With Supervised Agent</div>
              <div className={classes.underline} />
            </div>
          )}
          <MessageContainer messageList={displayedMessages} compact />
        </div>
      </ConsoleCard>
      <ConfirmDialog
        open={confirmDialogOpen && !isStaffInterventionHappening}
        content={'Send ASR incorrect event for word: "ASR MISSING"?'}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmBlankClick}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </div>
  );
};

export default LiveTranscriptCard;
